import React, {useContext, useEffect, useState} from 'react';

import {AppContext} from '../../AppModule/Context';

import AnimatedLogoComponent from '../common/genericComponents/animatedLogoComponent';
import ConsoltoSingleLogo from '../../assets/svgComponents/ConsoltoSingleLogo';
import VideoPlayerComponentV2 from '../common/genericComponents/redesign/videoPlayerComponentV2/videoPlayerComponentV2';
import MessageCue from '../common/messageCue/messageCue';

import postMessageService from '../../services/postMessageService';
import utilsService from '../../services/utilsService';

import VIDABOO_CONSTANTS from '../../config/consts';
import userDefaultImage from '../../assets/svg/user-default-icon.svg';

import './widgetMaskComponentV2.css';

const MASK_TEXT_FADE_ANIM_TIME = 0.5 * 1000;

const WidgetMaskComponentV2 = ({
  isDemo = false,
  isEntireWidgetHidden = true,
  isInitialWidgetTextShown = false,
  profileImage = null,
  widgetMaskText = null,
  widgetMaskType = null,
  widgetVideoBgUrl = null,
  widgetLocation = VIDABOO_CONSTANTS.WIDGET_LOCATION.BOTTOM_RIGHT,
  changeIframeSize = () => {},
  openWidget = () => {},
  setIsInitialWidgetTextShown = () => {},
}) => {
  const context = useContext(AppContext);

  const [maskText, setMaskText] = useState('');
  const [isMaskTextVisible, setIsMaskTextVisible] = useState(false);
  const [showMaskTextRemovalAnim, setShowMaskTextRemovalAnim] = useState(false);
  const [maskTextTimeoutId, setMaskTextTimeoutId] = useState(null);
  const [isMaskHoveredAtleastOnce, setIsMaskHoveredAtleastOnce] =
    useState(false);

  useEffect(() => {
    if (!isInitialWidgetTextShown) {
      setIsInitialWidgetTextShown();
      updateWidgetMaskText(true);
    } else {
      updateWidgetMaskText();
      setIsMaskHoveredAtleastOnce(true);
    }
    return () => {
      if (maskTextTimeoutId) {
        window.clearTimeout(maskTextTimeoutId);
      }
    };
  }, []);

  useEffect(() => {
    if (isDemo) {
      updateWidgetMaskText(true);
    }
  }, [widgetMaskType, widgetMaskText]);

  useEffect(() => {
    postMessageService.setSmallWidgetText(
      widgetMaskText ?? context.languageSet.CONSOLTO_WIDGET['LETS_TALK'],
    );
  }, [context.languageSet.CONSOLTO_WIDGET['LETS_TALK']]);

  useEffect(() => {
    handleIframeSize();
  }, [isMaskTextVisible]);

  const handleIframeSize = () => {
    let widgetSizeType = VIDABOO_CONSTANTS.WIDGET_SIZE.WIDGET_CLOSED;
    changeIframeSize(widgetSizeType, 'multiExpertWidget', true, {
      isMaskTextVisible,
    });
  };

  const getBgColorHex = () => {
    return utilsService.getColorHexByCssClass(
      '--et-widget-mask-background-color-fc',
    );
  };

  const updateWidgetMaskText = (isMount) => {
    let maskText =
      widgetMaskText ?? context.languageSet.CONSOLTO_WIDGET['LETS_TALK'];
    if (!maskText?.length) {
      maskText = null;
    }
    setMaskText(maskText);
    if (isMount) {
      setIsMaskTextVisible(!!maskText);
    }
  };

  const handleMouseEnter = () => {
    if (maskTextTimeoutId) {
      window.clearTimeout(maskTextTimeoutId);
    }
    if (maskText) {
      setIsMaskTextVisible(true);
      setShowMaskTextRemovalAnim(false);
    }
  };

  const handleMouseLeave = () => {
    if (maskTextTimeoutId) {
      window.clearTimeout(maskTextTimeoutId);
    }
    if (maskText) {
      setShowMaskTextRemovalAnim(true);
      const timeoutId = setTimeout(() => {
        setIsMaskTextVisible(false);
        setIsMaskHoveredAtleastOnce(true);
        setShowMaskTextRemovalAnim(false);
      }, MASK_TEXT_FADE_ANIM_TIME);
      setMaskTextTimeoutId(timeoutId);
    }
  };

  const getLogoUI = () => {
    return (
      <div className="et-wmc-main-mask-wrap">
        <div className="et-wmc-logo-mask" onClick={openWidget}>
          <AnimatedLogoComponent fillColor="#ffffff" />
        </div>
        {isMaskTextVisible && (
          <div
            className={`et-wmc-mask-text ${widgetLocation === VIDABOO_CONSTANTS.WIDGET_LOCATION.BOTTOM_CENTER ? 'et-wmc-mask-text-centered-logo-ui' : ''} ${isMaskHoveredAtleastOnce ? 'et-wmc-mask-text-visible' : ''} ${showMaskTextRemovalAnim ? 'et-wmc-mask-text-hidden' : ''}`}
            style={{
              borderColor: 'var(--et-widget-mask-background-color-fc)',
            }}
            onClick={openWidget}>
            {maskText}
          </div>
        )}
      </div>
    );
  };

  const getPhotoUI = () => {
    return (
      <div className="et-wmc-main-mask-wrap">
        <div
          className="et-wmc-photo-mask-img-wrap"
          style={{boxShadow: `0px 1px 10px 0px ${getBgColorHex()}`}}
          onClick={openWidget}>
          <img
            className={
              profileImage
                ? 'et-wmc-photo-mask-img'
                : 'et-wmc-photo-mask-img-default'
            }
            src={profileImage ?? userDefaultImage}
            alt=""
          />
        </div>
        {isMaskTextVisible && (
          <div
            className={`et-wmc-photo-mask-text ${isMaskHoveredAtleastOnce ? 'et-wmc-photo-mask-text-visible' : ''} ${showMaskTextRemovalAnim ? 'et-wmc-photo-mask-text-hidden' : ''}`}
            style={{boxShadow: `0px 1px 10px 0px ${getBgColorHex()}`}}
            onClick={openWidget}>
            {maskText}
          </div>
        )}
      </div>
    );
  };
  const getLetsTalkUI = () => {
    const widgetMaskWidth = showMaskTextRemovalAnim
      ? VIDABOO_CONSTANTS.WIDGET_MASK_DIMENSION.LETS_TALK.width
      : isMaskTextVisible
        ? utilsService.getWidgetMaskDimension(
            VIDABOO_CONSTANTS.WIDGET_MASK_TYPE.LETS_TALK,
            maskText,
          ).width
        : VIDABOO_CONSTANTS.WIDGET_MASK_DIMENSION.LETS_TALK.width;

    return (
      <div
        className={`et-wmc-lets-talk-wrap ${!maskText ? 'et-wmc-lets-talk-wrap-no-text' : ''} ${isMaskTextVisible && isMaskHoveredAtleastOnce ? 'et-wmc-lets-talk-wrap-open' : !isMaskHoveredAtleastOnce ? 'et-wmc-lets-talk-wrap-initial' : ''} ${isMaskTextVisible && showMaskTextRemovalAnim ? 'et-wmc-lets-talk-wrap-close' : ''}`}
        style={{boxShadow: `0px 1px 10px 0px ${getBgColorHex()}`}}
        onClick={openWidget}>
        <div className="et-wmc-lets-talk-overlay-wrap">
          <div className="et-wmc-ltow-bg" style={{width: widgetMaskWidth}} />
        </div>
        <div
          className={`et-wmc-lets-talk-img-wrap ${isMaskHoveredAtleastOnce || !maskText || (!isMaskHoveredAtleastOnce && showMaskTextRemovalAnim) ? 'et-wmc-lets-talk-img-wrap-with-txt' : ''}`}>
          <ConsoltoSingleLogo fillColor="#ffffff" />
        </div>
        {isMaskTextVisible && (
          <div className="et-wmc-lets-talk-mask-text" onClick={openWidget}>
            {maskText}
          </div>
        )}
      </div>
    );
  };

  const getSoloChat = () => {
    return (
      <div className="et-wmc-main-mask-wrap">
        <div
          className="et-wmc-solo-chat-mask"
          style={{boxShadow: `0px 1px 10px 0px ${getBgColorHex()}`}}
          onClick={openWidget}>
          <AnimatedLogoComponent fillColor="#ffffff" />
        </div>
        {isMaskTextVisible && (
          <div
            className={`et-wmc-solo-chat-mask-text et-wmc-mask-text  ${widgetLocation === VIDABOO_CONSTANTS.WIDGET_LOCATION.BOTTOM_CENTER ? 'et-wmc-mask-text-centered-solo-chat' : ''} ${isMaskHoveredAtleastOnce ? 'et-wmc-mask-text-visible' : ''} ${showMaskTextRemovalAnim ? 'et-wmc-mask-text-hidden' : ''}`}
            style={{borderColor: 'var(--et-widget-mask-background-color-fc)'}}
            onClick={openWidget}>
            {maskText}
          </div>
        )}
      </div>
    );
  };

  const getAnimatedPhotoUI = () => {
    return (
      <div className="et-wmc-main-mask-wrap">
        <div
          className={`et-wmc-animated-photo-mask ${
            utilsService.isMobileDevice()
              ? 'et-wmc-animated-photo-mask-mobile'
              : ''
          } `}
          style={{
            borderColor: 'var(--et-widget-mask-background-color-fc)',
          }}
          onClick={openWidget}>
          <div
            className="et-wmc-animated-photo-mask-ring"
            style={{
              borderColor: 'var(--et-widget-mask-background-color-fc)',
            }}
          />
          <>
            {widgetVideoBgUrl ? (
              <VideoPlayerComponentV2 videoSrcUrl={widgetVideoBgUrl} />
            ) : (
              <img
                className={
                  profileImage
                    ? 'et-wmc-animated-photo-mask-img'
                    : 'et-wmc-animated-photo-mask-img-default'
                }
                src={profileImage ?? userDefaultImage}
                alt=""
              />
            )}
          </>
        </div>
        {isMaskTextVisible && (
          <div
            className={`et-wmc-animated-photo-mask-text et-wmc-mask-text ${isMaskHoveredAtleastOnce ? 'et-wmc-mask-text-visible' : ''} ${showMaskTextRemovalAnim ? 'et-wmc-mask-text-hidden' : ''} ${widgetLocation === VIDABOO_CONSTANTS.WIDGET_LOCATION.BOTTOM_CENTER ? 'et-wmc-animated-photo-mask-text-centered' : ''}`}
            style={{
              background: 'var(--et-widget-mask-background-color)',
              borderColor: 'var(--et-widget-mask-background-color-fc)',
              boxShadow: `0px 1px 10px 0px ${getBgColorHex()}`,
            }}
            onClick={openWidget}>
            {maskText}
          </div>
        )}
      </div>
    );
  };

  const getMsgCueClassName = () => {
    switch (widgetMaskType) {
      case VIDABOO_CONSTANTS.WIDGET_MASK_TYPE.LOGO:
        return 'et-wmc-msg-cue-logo-mask';
      case VIDABOO_CONSTANTS.WIDGET_MASK_TYPE.PHOTO:
        return 'et-wmc-msg-cue-photo-mask';
      case VIDABOO_CONSTANTS.WIDGET_MASK_TYPE.LETS_TALK:
        return `et-wmc-msg-cue-lets-talk ${isMaskTextVisible && !showMaskTextRemovalAnim ? 'et-wmc-msg-cue-lets-talk-open' : ''} ${!maskText ? 'et-wmc-msg-cue-lets-talk-no-text' : ''}`;
      case VIDABOO_CONSTANTS.WIDGET_MASK_TYPE.SOLO_CHAT:
        return 'et-wmc-msg-cue-solo-chat';
      case VIDABOO_CONSTANTS.WIDGET_MASK_TYPE.ANIMATED_PHOTO:
        return 'et-wmc-msg-cue-animated-photo';
      default:
        return '';
    }
  };

  return !isEntireWidgetHidden ? (
    <div
      className={`et-widget-mask-container ${widgetLocation === VIDABOO_CONSTANTS.WIDGET_LOCATION.BOTTOM_LEFT ? 'et-widget-mask-container-left' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}>
      {!isDemo && (
        <div className={getMsgCueClassName()}>
          <MessageCue />
        </div>
      )}
      {widgetMaskType === VIDABOO_CONSTANTS.WIDGET_MASK_TYPE.LOGO ? (
        getLogoUI()
      ) : widgetMaskType === VIDABOO_CONSTANTS.WIDGET_MASK_TYPE.PHOTO ? (
        getPhotoUI()
      ) : widgetMaskType === VIDABOO_CONSTANTS.WIDGET_MASK_TYPE.LETS_TALK ? (
        getLetsTalkUI()
      ) : widgetMaskType === VIDABOO_CONSTANTS.WIDGET_MASK_TYPE.SOLO_CHAT ? (
        getSoloChat()
      ) : widgetMaskType ===
        VIDABOO_CONSTANTS.WIDGET_MASK_TYPE.ANIMATED_PHOTO ? (
        getAnimatedPhotoUI()
      ) : (
        <></>
      )}
    </div>
  ) : null;
};

export default WidgetMaskComponentV2;
